import React, { useRef, useState, useCallback, useEffect } from "react";
import { FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import { httpService } from "../../services/httpService";
import LoaderSpiner from "../LoaderSpiner/LoaderSpiner";
import config from "../../config";

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/avif",
  "image/heic",
  "image/heif",
  "image/svg+xml",
];

const DEFAULT_IMAGE_PLACEHOLDER =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 24 24' fill='%23f9fafb' stroke='%239ca3af' stroke-width='1.5'><rect width='18' height='18' x='3' y='3' rx='2' ry='2'/><circle cx='8.5' cy='8.5' r='1.5'/><path d='m21 15-5-5L5 21'/></svg>";

const S3_BASE_URL = (
  config.S3_BASE_URL ||
  process.env.REACT_APP_S3_BASE_URL ||
  "https://flps2025.s3.ap-south-1.amazonaws.com"
).replace(/\/+$/, "");

// ✅ Safely resolve any URL — avoids double-prefixing S3 base
const resolvePreviewUrl = (preview) => {
  if (!preview || typeof preview !== "string") return DEFAULT_IMAGE_PLACEHOLDER;
  if (preview.startsWith("blob:") || preview.startsWith("data:")) return preview;
  if (/^https?:\/\//i.test(preview)) return preview;
  return `${S3_BASE_URL}/${preview.replace(/^\/+/, "")}`;
};

const urlsToItems = (urls) => {
  if (!Array.isArray(urls)) return [];
  return urls
    .filter(Boolean)
    .map((url) => {
      const urlStr = typeof url === "string" ? url : (url?.url || "");
      if (!urlStr) return null;
      return {
        id: urlStr,
        url: urlStr,
        preview: urlStr,
        name: urlStr.split("/").pop().split("?")[0] || "image",
      };
    })
    .filter(Boolean);
};

export default function FileUpload({
  uploadEndpoint = "/upload/fileUpload",
  onUploadComplete,
  multiple = true,
  maxFiles = 10,
  existingUrls = [],
  moduleName = "vendors",
  docType = "service",
}) {
  const inputRef = useRef(null);
  const [isFileUploading, setIsFileUploading] = useState(false);
  const [items, setItems] = useState(() => urlsToItems(existingUrls));
  const itemsRef = useRef(items);
  const prevExistingUrlsRef = useRef(existingUrls);
  const initialFileCount = useRef(existingUrls.length);
  const [dragging, setDragging] = useState(false);

  // ✅ Sync with external existingUrls when they change (for editing existing services)
  useEffect(() => {
    // Check if existingUrls actually changed (not just reference)
    const hasChanged = 
      prevExistingUrlsRef.current.length !== existingUrls.length ||
      prevExistingUrlsRef.current.some((url, idx) => url !== existingUrls[idx]);
    
    if (hasChanged) {
      // Only update if we're not currently uploading
      if (!isFileUploading) {
        const newItems = urlsToItems(existingUrls);
        setItems(newItems);
        initialFileCount.current = existingUrls.length;
        prevExistingUrlsRef.current = existingUrls;
      }
    }
  }, [existingUrls, isFileUploading]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const clearItem = (id) => {
    setItems((prev) => {
      const updated = prev.filter((i) => i.id !== id);
      onUploadComplete?.(updated.map((i) => i.url));
      return updated;
    });
  };

  const processFiles = useCallback(
    async (fileList) => {
      if (!fileList || fileList.length === 0) return;

      setIsFileUploading(true);
      const files = Array.from(fileList);
      const toUpload = multiple ? files : files.slice(0, 1);

      // Check total file limit
      if (itemsRef.current.length + toUpload.length > maxFiles) {
        toast.error(
          `You can upload maximum ${maxFiles} image${maxFiles > 1 ? "s" : ""}`
        );
        setIsFileUploading(false);
        return;
      }

      // Validate file types
      const invalidFiles = toUpload.filter(
        (file) => !ALLOWED_IMAGE_TYPES.includes(file.type)
      );
      if (invalidFiles.length > 0) {
        toast.error(
          "Only image files are allowed (JPEG, PNG, GIF, WebP, SVG, HEIC, etc.)"
        );
        setIsFileUploading(false);
        return;
      }

      // Show loading state
      const previewItems = toUpload.map((file) => ({
        id: `preview-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
        isUploading: true,
      }));

      setItems((prev) => [...prev, ...previewItems]);

      const uploaded = [];
      const failed = [];

      // Upload one by one
      for (let i = 0; i < toUpload.length; i++) {
        const file = toUpload[i];
        const previewId = previewItems[i].id;

        const formData = new FormData();
        formData.append("module", moduleName);
        formData.append("docType", docType);
        formData.append("file", file);

        try {
          const resp = await httpService.post(uploadEndpoint, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });

          const s3Key = resp?.data?.s3Key || resp?.data?.url;
          if (!s3Key) throw new Error("No file URL returned");

          uploaded.push({ previewId, s3Key, name: file.name });
        } catch (err) {
          console.error("Upload failed:", err);
          toast.error(`Failed to upload ${file.name}`);
          failed.push(previewId);
          URL.revokeObjectURL(previewItems[i].preview);
        }
      }

      // Final state update
      setItems((prev) => {
        const uploadedMap = new Map(uploaded.map((u) => [u.previewId, u]));
        const failedSet = new Set(failed);

        const finalItems = prev
          .filter((item) => !failedSet.has(item.id))
          .map((item) => {
            if (uploadedMap.has(item.id)) {
              const { s3Key, name } = uploadedMap.get(item.id);
              if (item.preview?.startsWith("blob:")) {
                URL.revokeObjectURL(item.preview);
              }
              return { id: s3Key, url: s3Key, preview: s3Key, name };
            }
            return item;
          });

        // Notify parent with final URLs
        onUploadComplete?.(finalItems.map((i) => i.url));

        if (failed.length === 0 && uploaded.length > 0) {
          toast.success(
            `Successfully uploaded ${uploaded.length} image${uploaded.length > 1 ? "s" : ""}!`
          );
        } else if (uploaded.length > 0) {
          toast.info(`${uploaded.length} uploaded, ${failed.length} failed`);
        } else if (failed.length > 0) {
          toast.error("All uploads failed");
        }

        return finalItems;
      });

      setIsFileUploading(false);
    },
    [multiple, maxFiles, onUploadComplete, uploadEndpoint, moduleName, docType]
  );

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    processFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  const triggerFileInput = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const hasItems = items.length > 0;

  return (
    <div>
      {!hasItems ? (
        // ── Show BIG upload box when NO images ──
        <div
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-200
            ${
              dragging
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 hover:border-gray-400"
            }
          `}
          onClick={triggerFileInput}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <svg
                className="w-10 h-10 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 48 48"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900">
                Drop images here or{" "}
                <span className="text-blue-600 underline">click to browse</span>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Supported: JPEG, PNG, GIF, WebP, SVG, HEIC, AVIF
              </p>
            </div>
          </div>

          <input
            ref={inputRef}
            type="file"
            className="hidden"
            accept={ALLOWED_IMAGE_TYPES.join(",")}
            multiple={multiple}
            onChange={(e) =>
              e.target.files?.length && processFiles(e.target.files)
            }
          />
        </div>
      ) : (
        // ── Show thumbnails with "+" button when images exist ──
        <div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "flex-start" }}>
            {/* Existing Images */}
            {items.map((item, idx) => {
              const isUploading = item.isUploading || false;
              
              return (
                <div
                  key={item.id}
                  style={{
                    position: "relative",
                    width: idx === 0 ? "100%" : "80px",
                    height: idx === 0 ? "180px" : "80px",
                    borderRadius: "10px",
                    overflow: "hidden",
                    border: "1px solid #eef0f5",
                    background: "#f9fafb",
                    flexShrink: 0,
                  }}
                >
                  {isUploading ? (
                    <div style={{ 
                      width: "100%", 
                      height: "100%", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center",
                      background: "#f3f4f6"
                    }}>
                      <LoaderSpiner />
                    </div>
                  ) : (
                    <img
                      src={resolvePreviewUrl(item.preview)}
                      alt={item.name || "Upload preview"}
                      style={{ 
                        width: "100%", 
                        height: "100%", 
                        objectFit: "contain",
                        display: "block"
                      }}
                      onError={(e) => {
                        // ✅ Prevents infinite error loop / blinking
                        e.currentTarget.onerror = null;
                        const cur = e.currentTarget.src || "";
                        if (cur.includes("flps-local") && item.url) {
                          e.currentTarget.onerror = (err) => {
                            err.currentTarget.onerror = null;
                            err.currentTarget.src = DEFAULT_IMAGE_PLACEHOLDER;
                          };
                          e.currentTarget.src = `https://flps2025.s3.ap-south-1.amazonaws.com/${String(item.url).replace(/^\/+/, "")}`;
                          return;
                        }
                        e.currentTarget.src = DEFAULT_IMAGE_PLACEHOLDER;
                      }}
                    />
                  )}
                  
                  <button
                    onClick={() => clearItem(item.id)}
                    title="Remove image"
                    style={{
                      position: "absolute",
                      top: "4px",
                      right: "4px",
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      background: "rgba(17,24,39,0.7)",
                      color: "#fff",
                      border: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    <FiX size={14} />
                  </button>
                  
                  {idx === 0 && items.length > 1 && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: "8px",
                        left: "8px",
                        background: "rgba(0,0,0,0.7)",
                        color: "#fff",
                        fontSize: "11px",
                        padding: "3px 12px",
                        borderRadius: "4px",
                        fontWeight: 600,
                        letterSpacing: "0.5px"
                      }}
                    >
                      COVER
                    </div>
                  )}
                </div>
              );
            })}

            {/* Add More Button - Show in the same row as thumbnails (after the cover image) */}
            {items.length < maxFiles && items.length > 1 && (
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "10px",
                  border: "2px dashed #d1d5db",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  background: "#fafafa",
                  transition: "all 0.2s",
                  flexShrink: 0,
                  marginTop: "0px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f3f4f6";
                  e.currentTarget.style.borderColor = "#9ca3af";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#fafafa";
                  e.currentTarget.style.borderColor = "#d1d5db";
                }}
                onClick={triggerFileInput}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <span style={{ fontSize: "28px", color: "#9ca3af", lineHeight: 1 }}>
                  +
                </span>
                <span style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px", fontWeight: 500 }}>
                  Add Image
                </span>
              </div>
            )}
          </div>

          {/* If there's only 1 image (cover only), show the Add button in a new row */}
          {items.length === 1 && items.length < maxFiles && (
            <div style={{ marginTop: "8px" }}>
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "10px",
                  border: "2px dashed #d1d5db",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  background: "#fafafa",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#f3f4f6";
                  e.currentTarget.style.borderColor = "#9ca3af";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#fafafa";
                  e.currentTarget.style.borderColor = "#d1d5db";
                }}
                onClick={triggerFileInput}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <span style={{ fontSize: "28px", color: "#9ca3af", lineHeight: 1 }}>
                  +
                </span>
                <span style={{ fontSize: "11px", color: "#9ca3af", marginTop: "2px", fontWeight: 500 }}>
                  Add Image
                </span>
              </div>
            </div>
          )}

          {/* Hidden File Input */}
          <input
            ref={inputRef}
            type="file"
            style={{ display: "none" }}
            accept={ALLOWED_IMAGE_TYPES.join(",")}
            multiple={multiple}
            onChange={(e) =>
              e.target.files?.length && processFiles(e.target.files)
            }
          />
        </div>
      )}
    </div>
  );
}